import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, apiError } from "@/lib/api";
import { log } from "@/lib/log";

const AppContext = createContext(null);
const THEME_KEY = "archedia-theme";

const EMPTY = { properties: [], leads: [], clients: [], interests: [], siteVisits: [], followUps: [], enquiries: [], documents: [], team: [], activityLog: [], testimonials: [], blogs: [], blogCategories: [], leadCounter: 0, settings: {} };

function loadTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    if (t) return t;
  } catch (e) { log.warn("localStorage unavailable", e); }
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

export function AppProvider({ children }) {
  const [theme, setThemeState] = useState(loadTheme);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem(THEME_KEY, theme); } catch (e) { log.warn("localStorage unavailable", e); }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  const loadPublic = useCallback(async () => {
    try {
      const { data: d } = await api.get("/public/bootstrap");
      setData((prev) => ({ ...prev, properties: d.properties, team: d.team, testimonials: d.testimonials || [], blogs: d.blogs || [], blogCategories: d.blogCategories || [], settings: d.settings }));
    } catch (e) { log.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadAdmin = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/data");
      setData(d);
    } catch (e) { log.error(e); }
  }, []);

  const refresh = useCallback(() => (currentUser ? loadAdmin() : loadPublic()), [currentUser, loadAdmin, loadPublic]);

  useEffect(() => {
    (async () => {
      if (localStorage.getItem(TOKEN_KEY)) {
        try {
          const { data: u } = await api.get("/auth/me");
          setCurrentUser(u);
          await loadAdmin();
          setLoading(false);
        } catch (e) {
          localStorage.removeItem(TOKEN_KEY);
          await loadPublic();
        }
      } else {
        await loadPublic();
      }
      setAuthChecked(true);
    })();
  }, [loadAdmin, loadPublic]);

  // ---- Auth (admin only) ----
  const login = useCallback(async (email, password) => {
    try {
      const { data: r } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, r.token);
      setCurrentUser(r.user);
      await loadAdmin();
      return { ok: true, user: r.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  }, [loadAdmin]);

  const logout = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    setCurrentUser(null);
    setData(EMPTY);
    await loadPublic();
  }, [loadPublic]);

  // ---- Public writes ----
  const addLead = useCallback(async (payload) => {
    const { data: r } = await api.post("/public/leads", payload);
    return r.id;
  }, []);

  const addItem = useCallback(async (coll, item) => {
    const path = currentUser ? `/admin/${coll}` : `/public/${coll}`;
    const { data: r } = await api.post(path, item);
    if (currentUser) await loadAdmin();
    return r.id;
  }, [currentUser, loadAdmin]);

  // ---- Admin writes ----
  const updateLead = useCallback(async (id, patch, activityText) => {
    await api.patch(`/admin/leads/${id}`, { patch, activityText });
    await loadAdmin();
  }, [loadAdmin]);

  const updateItem = useCallback(async (coll, id, patch) => {
    await api.patch(`/admin/${coll}/${id}`, patch);
    await loadAdmin();
  }, [loadAdmin]);

  const removeItem = useCallback(async (coll, id) => {
    await api.delete(`/admin/${coll}/${id}`);
    await loadAdmin();
  }, [loadAdmin]);

  const addProperty = useCallback(async (prop) => {
    const { data: r } = await api.post("/admin/properties", { ...prop, createdAt: new Date().toISOString() });
    await loadAdmin();
    return r.id;
  }, [loadAdmin]);

  const updateProperty = useCallback((id, patch) => updateItem("properties", id, patch), [updateItem]);
  const deleteProperty = useCallback((id) => removeItem("properties", id), [removeItem]);

  const duplicateProperty = useCallback(async (id) => {
    const src = data.properties.find((p) => p.id === id);
    if (!src) return;
    const copy = { ...src, project: `${src.project} (Copy)`, featured: false };
    delete copy.id;
    await addProperty(copy);
  }, [data.properties, addProperty]);

  const updateClient = useCallback(async (id, patch, activityText) => {
    const c = data.clients.find((x) => x.id === id);
    const body = { ...patch };
    if (activityText) body.activity = [{ date: new Date().toISOString(), text: activityText }, ...((c && c.activity) || [])];
    await updateItem("clients", id, body);
  }, [data.clients, updateItem]);

  const logActivity = useCallback(async (text, actor = "Admin", type = "system") => {
    if (!currentUser) return;
    await api.post("/admin/activity", { text, actor, type });
    await loadAdmin();
  }, [currentUser, loadAdmin]);

  const resetData = useCallback(async () => {
    await api.post("/admin/reset");
    await loadAdmin();
  }, [loadAdmin]);

  const updateSettings = useCallback(async (patch) => {
    const { data: s } = await api.patch("/admin/settings", patch);
    setData((d) => ({ ...d, settings: s }));
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await api.post("/auth/change-password", { currentPassword, newPassword });
      return { ok: true };
    } catch (e) { return { ok: false, error: apiError(e) }; }
  }, []);

  const value = {
    theme, setTheme, toggleTheme,
    data, loading, authChecked, refresh,
    currentUser, login, logout, changePassword, updateSettings,
    addLead, updateLead,
    addProperty, updateProperty, deleteProperty, duplicateProperty,
    addItem, updateItem, removeItem,
    updateClient, logActivity, resetData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
